import { useState, useCallback, useMemo } from "react";
import { now } from "@freightsimple/time/helpers";
import {
  loadColumnConfigurations,
  saveColumnConfiguration,
  updateColumnConfiguration,
  deleteColumnConfiguration,
} from "./columnConfigurations";
import type { SavedColumnConfiguration } from "./columnConfigurations";

export interface ColumnConfigurationActions {
  configurations: SavedColumnConfiguration[];
  activeConfigId: string | undefined;
  activeConfigName: string | undefined;
  isDirty: boolean;
  handleSaveConfiguration: (name: string) => void;
  handleUpdateConfiguration: (configId: string) => void;
  handleLoadConfiguration: (configId: string) => void;
  handleDeleteConfiguration: (configId: string) => void;
  handleDuplicateConfiguration: (newName: string) => void;
}

export function useColumnConfigurations(
  tableId: string | undefined,
  hiddenKeys: Set<string>,
  columnOrder: string[],
  sortKey: string | undefined,
  sortDir: "asc" | "desc",
  setHiddenKeys: (keys: Set<string>) => void,
  setColumnOrder: (order: string[]) => void,
  setSort: (key: string | undefined, dir: "asc" | "desc") => void,
): ColumnConfigurationActions {
  const [configurations, setConfigurations] = useState<
    SavedColumnConfiguration[]
  >(() => (tableId ? loadColumnConfigurations(tableId) : []));
  const [activeConfigId, setActiveConfigIdRaw] = useState<string | undefined>(
    () => {
      // Restore from URL param on mount
      const params = new URLSearchParams(window.location.search);
      return params.get("configId") ?? undefined;
    },
  );

  const setActiveConfigId = useCallback((configId: string | undefined) => {
    setActiveConfigIdRaw(configId);
    const url = new URL(window.location.href);
    if (configId) {
      url.searchParams.set("configId", configId);
    } else {
      url.searchParams.delete("configId");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  const activeConfig = configurations.find(
    (c) => c.configId === activeConfigId,
  );
  const activeConfigName = activeConfig?.configName;

  const isDirty = useMemo(() => {
    if (!activeConfig) return false;
    const savedHidden = new Set(activeConfig.columnConfig.hiddenKeys);
    if (hiddenKeys.size !== savedHidden.size) return true;
    for (const k of hiddenKeys) {
      if (!savedHidden.has(k)) return true;
    }
    if (
      JSON.stringify(columnOrder) !==
      JSON.stringify(activeConfig.columnConfig.columnOrder)
    )
      return true;
    const savedSort = activeConfig.sortConfig;
    if (sortKey !== (savedSort?.sortKey ?? undefined)) return true;
    if (sortKey && sortDir !== (savedSort?.sortDir ?? "asc")) return true;
    return false;
  }, [activeConfig, hiddenKeys, columnOrder, sortKey, sortDir]);

  const handleSaveConfiguration = useCallback(
    (configName: string) => {
      if (!tableId) return;
      const config: SavedColumnConfiguration = {
        configId: crypto.randomUUID(),
        configName,
        columnConfig: {
          hiddenKeys: Array.from(hiddenKeys),
          columnOrder,
        },
        ...(sortKey ? { sortConfig: { sortKey, sortDir } } : {}),
        createdAt: now(),
      };
      const updated = saveColumnConfiguration(tableId, config);
      setConfigurations(updated);
      setActiveConfigId(config.configId);
    },
    [tableId, hiddenKeys, columnOrder, sortKey, sortDir],
  );

  const handleUpdateConfiguration = useCallback(
    (configId: string) => {
      if (!tableId) return;
      const updated = updateColumnConfiguration(tableId, configId, {
        columnConfig: {
          hiddenKeys: Array.from(hiddenKeys),
          columnOrder,
        },
        ...(sortKey ? { sortConfig: { sortKey, sortDir } } : {}),
      });
      setConfigurations(updated);
    },
    [tableId, hiddenKeys, columnOrder, sortKey, sortDir],
  );

  const handleLoadConfiguration = useCallback(
    (configId: string) => {
      const config = configurations.find((c) => c.configId === configId);
      if (!config) return;
      setHiddenKeys(new Set(config.columnConfig.hiddenKeys));
      setColumnOrder(config.columnConfig.columnOrder);
      setSort(config.sortConfig?.sortKey, config.sortConfig?.sortDir ?? "asc");
      setActiveConfigId(configId);
    },
    [configurations, setHiddenKeys, setColumnOrder, setSort],
  );

  const handleDeleteConfiguration = useCallback(
    (configId: string) => {
      if (!tableId) return;
      const updated = deleteColumnConfiguration(tableId, configId);
      setConfigurations(updated);
      if (activeConfigId === configId) setActiveConfigId(undefined);
    },
    [tableId, activeConfigId],
  );

  const handleDuplicateConfiguration = useCallback(
    (newName: string) => {
      if (!tableId || !activeConfig) return;
      const config: SavedColumnConfiguration = {
        configId: crypto.randomUUID(),
        configName: newName,
        columnConfig: {
          hiddenKeys: Array.from(hiddenKeys),
          columnOrder,
        },
        ...(sortKey ? { sortConfig: { sortKey, sortDir } } : {}),
        createdAt: now(),
      };
      const updated = saveColumnConfiguration(tableId, config);
      setConfigurations(updated);
      setActiveConfigId(config.configId);
    },
    [tableId, activeConfig, hiddenKeys, columnOrder, sortKey, sortDir],
  );

  return {
    configurations,
    activeConfigId,
    activeConfigName,
    isDirty,
    handleSaveConfiguration,
    handleUpdateConfiguration,
    handleLoadConfiguration,
    handleDeleteConfiguration,
    handleDuplicateConfiguration,
  };
}
